import { useQuery } from '@tanstack/react-query'

async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export interface OverviewResponse {
  freeBerths: number
  occupiedBerths: number
  pendingCount: number
  waitlistedCount: number
  monthRevenueIdr: string
  recentBookings: {
    id: string
    code: string
    status: string
    checkInAt: string
    checkOutAt: string
    customer: { firstName: string; lastName: string; category: string }
    boat: { name: string }
    berth: { code: string } | null
  }[]
  pendingRequests: {
    id: string
    code: string
    status: string
    checkInAt: string
    customer: { firstName: string; lastName: string; category: string }
    boat: { name: string; type?: string }
    createdAt: string
  }[]
}

export function useAnalyticsOverview() {
  return useQuery({
    queryKey: ['analytics', 'overview'],
    queryFn: () => apiFetch<OverviewResponse>('/api/analytics/overview'),
    staleTime: 30_000,
  })
}

export interface BoatsAnalytics {
  boatsDepartedToday: number
  boatsArrivedToday: number
  currentlyDocked: number
  inDryDock: number
  byType: { type: string; count: number }[]
  sizeDistribution: { small: number; medium: number; large: number; total: number }
  delayedArrivals: {
    id: string
    code: string
    boatName: string
    customerName: string
    berth: string
    arrivalDate: string
    minutesDelayed: number
    daysDelayed: number
  }[]
  frequentVisitingBoats: {
    boatId: string
    name: string
    customerName: string
    visitCount: number
    lastVisit: string
  }[]
  customersWithoutBoats: {
    id: string
    firstName: string
    lastName: string
    email: string | null
  }[]
}

export function useAnalyticsBoats() {
  return useQuery({
    queryKey: ['analytics', 'boats'],
    queryFn: () => apiFetch<BoatsAnalytics>('/api/analytics/boats'),
    staleTime: 60_000,
  })
}

export interface RevenueAnalytics {
  buckets: { month: string; revenue: number }[]
  statusMix: { status: string; count: number; total: string }[]
}

export function useAnalyticsRevenue(months = 12) {
  return useQuery({
    queryKey: ['analytics', 'revenue', months],
    queryFn: () => apiFetch<RevenueAnalytics>(`/api/analytics/revenue?months=${months}`),
    staleTime: 60_000,
  })
}

export interface CustomersAnalytics {
  totalCustomers: number
  byCategory: { category: string; count: number }[]
  topByRevenue: { customerId: string; name: string; total: string }[]
}

export function useAnalyticsCustomers() {
  return useQuery({
    queryKey: ['analytics', 'customers'],
    queryFn: () => apiFetch<CustomersAnalytics>('/api/analytics/customers'),
    staleTime: 60_000,
  })
}

export interface BoatDocumentsAnalytics {
  total: number
  withRegistration: number
  withImo: number
  withHullId: number
  withFlagState: number
  missingDocs: {
    id: string
    name: string
    customerName: string
    registration: string | null
    imoNumber: string | null
    hullId: string | null
    flagState: string | null
    missing: string[]
  }[]
}

export function useAnalyticsBoatDocuments() {
  return useQuery({
    queryKey: ['analytics', 'boat-documents'],
    queryFn: () => apiFetch<BoatDocumentsAnalytics>('/api/analytics/boat-documents'),
    staleTime: 60_000,
  })
}

export interface CustomerDocumentsAnalytics {
  total: number
  withIdNumber: number
  withTaxId: number
  expired: {
    id: string
    firstName: string
    lastName: string
    email: string | null
    idNumber: string | null
    idExpiry: string | null
  }[]
  expiringSoon: {
    id: string
    firstName: string
    lastName: string
    email: string | null
    idNumber: string | null
    idExpiry: string | null
  }[]
  expiredCount: number
  expiringSoonCount: number
}

export function useAnalyticsCustomerDocuments() {
  return useQuery({
    queryKey: ['analytics', 'customer-documents'],
    queryFn: () => apiFetch<CustomerDocumentsAnalytics>('/api/analytics/customer-documents'),
    staleTime: 60_000,
  })
}
